/* global wombatWidgetsConfig */

/**
 * Modifies the settings widget by adding a "Sign Out" item if it doesn't exist.
 *
 * @param {HTMLElement} target The settings widget container element.
 *
 * @return {void}
 */
export const addSignOutDesktopItem = ( target ) => {

	if ( ! target ) {

		return;

	}

	let item = target.querySelector( '.js-go-signout-item' );

	if ( ! item ) {

		item = document.createElement( 'div' );
		item.className = 'MuiButtonBase-root MuiListItem-root MuiListItem-gutters MuiListItem-button';
		item.classList.add( 'js-go-signout-item' );

		const itemText = document.createElement( 'div' );
		itemText.className = 'MuiListItemText-root';

		const itemLabel = document.createElement( 'span' );
		itemLabel.className = 'MuiTypography-root MuiListItemText-primary MuiTypography-body2 MuiTypography-displayBlock';
		itemLabel.style.marginLeft = '16px';
		itemLabel.style.fontWeight = '400';
		itemLabel.textContent = 'Sign Out';

		itemText.appendChild( itemLabel );
		item.appendChild( itemText );
		target.appendChild( item );

	}

	item.tabIndex = 0;
	item.setAttribute( 'tabindex', '0' );
	item.setAttribute( 'role', 'button' );
	item.setAttribute( 'aria-disabled', 'false' );
	item.setAttribute( 'aria-label', 'Sign Out' );

	if ( item.dataset.goSignoutHandlerExists !== '1' ) {

		item.dataset.goSignoutHandlerExists = '1';

		item.addEventListener( 'click', () => {

			// eslint-disable-next-line no-undef, camelcase
			if ( typeof go_fw !== 'undefined' && typeof go_fw.logout === 'function' ) {

				// eslint-disable-next-line no-undef, camelcase
				go_fw.logout();

			}

		} );

		item.addEventListener( 'keydown', ( event ) => {

			if ( event.key !== 'Enter' && event.key !== ' ' ) {

				return;

			}

			event.preventDefault();
			item.click();

		} );

	}

};

/**
 * Modifies the mobile settings menu by adding a "Sign Out" item if it doesn't exist.
 *
 * @param {HTMLElement} target The mobile settings menu list element.
 *
 * @return {void}
 */
export const addSignOutMobileItem = ( target ) => {

	if ( ! target ) {

		return;

	}

	let item = target.querySelector( '.js-go-signout-menu-item' );

	if ( ! item ) {

		item = document.createElement( 'li' );
		item.className = 'MuiButtonBase-root MuiListItem-root MuiMenuItem-root MuiMenuItem-gutters MuiListItem-gutters MuiListItem-button';
		item.classList.add( 'js-go-signout-menu-item' );
		item.setAttribute( 'data-value', 'settings_signout' );
		item.textContent = 'Sign Out';
		target.appendChild( item );

	}

	item.tabIndex = 0;
	item.setAttribute( 'tabindex', '0' );
	item.setAttribute( 'role', 'option' );
	item.setAttribute( 'aria-disabled', 'false' );

	if ( item.dataset.goSignoutHandlerExists !== '1' ) {

		item.dataset.goSignoutHandlerExists = '1';

		item.addEventListener( 'click', () => {

			// eslint-disable-next-line no-undef, camelcase
			if ( typeof go_fw !== 'undefined' && typeof go_fw.logout === 'function' ) {

				// eslint-disable-next-line no-undef, camelcase
				go_fw.logout();

			}

		} );

		item.addEventListener( 'keydown', ( event ) => {

			if ( event.key !== 'Enter' && event.key !== ' ' ) {

				return;

			}

			event.preventDefault();
			item.click();

		} );

	}

};

/**
 *
 * @param {HTMLElement} target
 * @return {void}
 */
export const changeRegisterWidgetsTexts = ( target ) => {

	if ( ! target ) {

		return;

	}

	const heading = target.querySelector( 'h2.MuiTypography-root' );
	if ( ! heading ) {

		return;

	}

	heading.textContent = 'Create Account';

}

/**
 * Makes the register widget's Date of Birth field open the existing
 * go_fw datepicker when the field itself is clicked or activated.
 *
 * @param {HTMLElement} target Rendered register widget root.
 * @return {void}
 */
export const enableRegisterDateOfBirthPicker = ( target ) => {

	if ( ! target ) {

		return;

	}

	/**
	 * Binds the existing widget datepicker trigger once the DOB controls exist.
	 *
	 * @return {boolean} True when the binding was attached.
	 */
	const bindDatePicker = () => {

		const dateInput = target.querySelector( 'input[aria-label="Date of Birth"]' );
		const changeDateButton = target.querySelector( 'button[aria-label="Change Date Of Birth"]' );

		if ( ! dateInput || ! changeDateButton ) {

			return false;

		}

		if ( dateInput.dataset.goDatePickerBound === '1' ) {

			return true;

		}

		dateInput.dataset.goDatePickerBound = '1';
		dateInput.readOnly = true;
		dateInput.setAttribute( 'aria-haspopup', 'dialog' );

		/**
		 * Opens the widget's existing datepicker trigger.
		 *
		 * @return {void}
		 */
		const openDatePicker = () => {

			changeDateButton.click();

		};

		dateInput.addEventListener( 'keydown', ( event ) => {

			if ( event.key !== 'Enter' && event.key !== ' ' && event.key !== 'ArrowDown' ) {

				return;

			}

			event.preventDefault();
			openDatePicker();

		} );

		// The input root also wraps the input and the calendar adornment, so this
		// single listener covers clicks on both without opening the picker twice.
		const inputRoot = dateInput.closest( '.MuiInputBase-root' );

		if ( inputRoot && inputRoot.dataset.goDatePickerBound !== '1' ) {

			inputRoot.dataset.goDatePickerBound = '1';
			inputRoot.addEventListener( 'click', ( event ) => {

				if ( event.target.closest( 'button' ) ) {

					return;

				}

				openDatePicker();

			} );

		}

		return true;

	};

	if ( bindDatePicker() ) {

		return;

	}

	if ( target.dataset.goDatePickerObserverBound === '1' ) {

		return;

	}

	target.dataset.goDatePickerObserverBound = '1';

	const observer = new MutationObserver( () => {

		if ( ! bindDatePicker() ) {

			return;

		}

		observer.disconnect();
		delete target.dataset.goDatePickerObserverBound;

	} );

	observer.observe( target, { childList: true, subtree: true } );

}

/**
 *
 * @param {HTMLElement} target
 * @return {void}
 */
export const changeLoginWidgetsTexts = ( target ) => {

	if ( ! target ) {

		return;

	}

	const beforeButtonText = target.querySelector( '.MuiGrid-root.MuiGrid-item > div > p.MuiTypography-root' );
	const button = target.querySelector( '.MuiGrid-root.MuiGrid-item > div > button.MuiButtonBase-root' );
	const buttonText = button?.querySelector( '.MuiGrid-root.MuiGrid-item > div > button.MuiButtonBase-root .MuiButton-label span' );
	const resendButton = target.querySelector( '.MuiGrid-root.MuiGrid-item > button.MuiButton-text' );
	const resendButtonText = resendButton?.querySelector( '.MuiButton-label p' );
	const { resendActivation, signInSupport } = wombatWidgetsConfig?.texts ?? {};
	const { email, phone } = wombatWidgetsConfig?.support ?? {};

	if ( beforeButtonText ) {

		beforeButtonText.textContent = 'Don\'t Have An Account?';

	}

	if ( button && buttonText ) {

		button.setAttribute( 'aria-label', 'Create Account' );
		button.setAttribute( 'title', 'Create Account' );
		buttonText.textContent = 'Create Account';

	}

	if ( resendButton && resendButtonText && resendActivation ) {

		resendButton.setAttribute( 'aria-label', resendActivation );
		resendButton.setAttribute( 'title', resendActivation );
		resendButtonText.textContent = resendActivation;

	}

	if ( resendButton && signInSupport && email && phone && ! target.querySelector( '.b-widgets__support' ) ) {

		const links = {
			'%1$s': createLink( `mailto:${ email }`, email ),
			'%2$s': createLink( `tel:${ phone.replace( /[^\d+]/g, '' ) }`, phone ),
		};
		const support = document.createElement( 'p' );
		support.className = 'b-widgets__support';

		signInSupport.split( /(%[12]\$s)/ ).forEach( ( part ) => {

			support.append( links[ part ] ?? part );

		} );

		resendButton.before( support );

	}

}

/**
 * Creates a link element with plain text content.
 *
 * @param {string} href Link URL.
 * @param {string} text Link text.
 * @return {HTMLAnchorElement}
 */
const createLink = ( href, text ) => {

	const link = document.createElement( 'a' );
	link.href = href;
	link.textContent = text;

	return link;

};

/**
 * Changes the texts in the forgot password widget.
 * @param {HTMLElement} target
 */
export const changeForgotPasswordWidgetsTexts = ( target ) => {

	if ( ! target ) {

		return;

	}

	const heading = target.querySelector( '.MuiCardContent-root h2.MuiTypography-root' );
	const button = target.querySelector( '.MuiCardContent-root > div > a.MuiButtonBase-root' );
	const buttonText = button?.querySelector( 'p.MuiTypography-root' );
	const beforeButtonText = button?.previousElementSibling;
	const submitButtonText = target.querySelector( '.MuiCardContent-root > div > button.MuiButtonBase-root span.MuiButton-label' );

	if ( heading ) {

		heading.textContent = 'Reset Password';

	}

	if ( submitButtonText ) {

		submitButtonText.textContent = 'Send Instructions'

	}

	if ( beforeButtonText ) {

		beforeButtonText.textContent = 'Don\'t Have An Account?';

	}

	if ( button && buttonText ) {

		button.setAttribute( 'aria-label', 'Create Account' );
		button.setAttribute( 'title', 'Create Account' );
		buttonText.textContent = 'Create Account';

	}

}
